import { useEffect, useMemo, useState } from "react";

/**
 * Client-side pagination for in-memory lists (IR sessions, POAM, activity, etc.).
 * Pass `resetKey` (e.g. filter value) to jump back to page 1 when the dataset changes.
 */
export function useClientPagination(items, defaultPageSize = 10, resetKey = null) {
  const list = Array.isArray(items) ? items : [];
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  useEffect(() => {
    setPage(1);
  }, [list.length, pageSize, resetKey]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(list.length / pageSize) || 1);
    if (page > totalPages) setPage(totalPages);
  }, [list.length, page, pageSize]);

  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;

  const pageItems = useMemo(
    () => list.slice(start, start + pageSize),
    [list, start, pageSize]
  );

  const from = total === 0 ? 0 : start + 1;
  const to = Math.min(start + pageSize, total);

  return {
    page: safePage,
    setPage,
    pageSize,
    setPageSize,
    total,
    totalPages,
    pageItems,
    from,
    to,
  };
}

export default useClientPagination;
